import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { URLS } from '../../resources/constants';
import { PayerDataProvider, usePayerData } from '../../resources/hooks/usePayerDataHook';

const Tax1099LayoutInner: React.FC = () => {
  const { t } = useTranslation('Tax1099Nav');
  const location = useLocation();
  const path = location.pathname;
  const { businessName, maskedEin, isConfigured } = usePayerData();

  let activeTab: 'portfolio' | 'vendors' | 'expenses' | 'payees' | 'forms' | 'yearend' | 'payer' = 'vendors';
  if (path.includes('payee-review')) {
    activeTab = 'payees';
  } else if (path.includes('1099-forms')) {
    activeTab = 'forms';
  } else if (path.includes('portfolio')) {
    activeTab = 'portfolio';
  } else if (path.includes('vendor')) {
    activeTab = 'vendors';
  } else if (path.includes('expense') || path.includes('payment')) {
    activeTab = 'expenses';
  } else if (path.includes('year-end')) {
    activeTab = 'yearend';
  } else if (path.includes('payer-settings')) {
    activeTab = 'payer';
  }

  return (
    <div className="d-flex" style={{ minHeight: 'calc(100vh - 70px)', backgroundColor: '#f8fafc' }}>
      {/* 1099 Compliance Side Menu Bar matching mockup */}
      <aside
        className="d-none d-md-flex flex-column gap-3 p-3 text-white"
        style={{
          width: '260px',
          minWidth: '260px',
          background:
            'radial-gradient(circle at 10% 10%, rgba(16, 161, 220, .18), transparent 45%), linear-gradient(180deg, #103e7b 0%, #0d2c55 100%)',
          borderRight: '1px solid #dfe7f0',
        }}
      >
        {/* Brand Block */}
        <div style={{ padding: '6px 6px 14px', borderBottom: '1px solid rgba(255,255,255,.16)' }}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                color: '#14509e',
                fontSize: '13px',
              }}
            >
              RX
            </div>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: '#ffffff',
                textTransform: 'uppercase',
              }}
            >
              {t('brand.platform')}
            </span>
          </div>
          <div
            className="d-flex align-items-center gap-2 text-white"
            style={{ fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            <span style={{ fontSize: '17px' }}>{t('brand.ready')}</span>
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '999px',
                background: 'rgba(16,161,220,.25)',
                color: '#c8efff',
                fontSize: '10px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 700,
              }}
            >
              {t('brand.compliance')}
            </span>
          </div>
          <p style={{ fontSize: '11px', color: '#bfdbfe', marginTop: '4px', marginBottom: 0 }}>
            {t('brand.subtitle')}
          </p>

          {/* Active Payer Entity Card */}
          <div
            style={{
              marginTop: '12px',
              padding: '10px 12px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.18)',
            }}
          >
            <div
              style={{
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#bfdbfe',
                fontWeight: 800,
              }}
            >
              {t('payer.active_entity')}
            </div>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 800,
                color: '#ffffff',
                marginTop: '3px',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={businessName}
            >
              🏢 {businessName}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: '#93c5fd',
                marginTop: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span className="font-monospace fw-bold">{t('payer.ein', { ein: maskedEin || t('payer.none') })}</span>
              {isConfigured ? (
                <span
                  style={{
                    fontSize: '9px',
                    background: '#16a34a',
                    color: '#fff',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontWeight: 800,
                  }}
                >
                  {t('payer.verified')}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: '9px',
                    background: '#f59e0b',
                    color: '#fff',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontWeight: 800,
                  }}
                >
                  {t('payer.setup_needed')}
                </span>
              )}
            </div>
          </div>
        </div>


        {/* 1099 Navigation Links */}
        <nav className="d-flex flex-column gap-2 mt-2">
          <div
            style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#bfdbfe',
              fontWeight: 800,
              padding: '8px 6px 0',
            }}
          >
            {t('section.overview')}
          </div>

          <Link
            to={URLS.TAX_1099_PORTFOLIO}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '0 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: activeTab === 'portfolio' ? '#14509e' : 'rgba(255,255,255,.85)',
              background: activeTab === 'portfolio' ? '#ffffff' : 'transparent',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: activeTab === 'portfolio' ? '0 4px 14px rgba(0,0,0,.1)' : 'none',
              transition: 'all .15s',
            }}
          >
            <span
              style={{
                width: '24px',
                height: '24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                background: activeTab === 'portfolio' ? '#edf6fc' : 'rgba(255,255,255,.12)',
                color: activeTab === 'portfolio' ? '#14509e' : '#ffffff',
                fontSize: '11px',
                fontWeight: 800,
              }}
            >
              01
            </span>
            <span>{t('menu.firm_portfolio')}</span>
          </Link>

          <div
            style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#bfdbfe',
              fontWeight: 800,
              padding: '8px 6px 0',
            }}
          >
            {t('section.setup')}
          </div>

          <Link
            to={URLS.TAX_1099_PAYER_SETTINGS}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '0 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: activeTab === 'payer' ? '#14509e' : 'rgba(255,255,255,.85)',
              background: activeTab === 'payer' ? '#ffffff' : 'transparent',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: activeTab === 'payer' ? '0 4px 14px rgba(0,0,0,.1)' : 'none',
              transition: 'all .15s',
            }}
          >
            <span
              style={{
                width: '24px',
                height: '24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                background: activeTab === 'payer' ? '#edf6fc' : 'rgba(255,255,255,.12)',
                color: activeTab === 'payer' ? '#14509e' : '#ffffff',
                fontSize: '11px',
                fontWeight: 800,
              }}
            >
              02
            </span>
            <span>{t('menu.payer_settings')}</span>
          </Link>

          <div
            style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#bfdbfe',
              fontWeight: 800,
              padding: '8px 6px 0',
            }}
          >
            {t('section.vendors_payments')}
          </div>

          <Link
            to={URLS.VENDOR_CENTER}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '0 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: activeTab === 'vendors' ? '#14509e' : 'rgba(255,255,255,.85)',
              background: activeTab === 'vendors' ? '#ffffff' : 'transparent',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: activeTab === 'vendors' ? '0 4px 14px rgba(0,0,0,.1)' : 'none',
              transition: 'all .15s',
            }}
          >
            <span
              style={{
                width: '24px',
                height: '24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                background: activeTab === 'vendors' ? '#edf6fc' : 'rgba(255,255,255,.12)',
                color: activeTab === 'vendors' ? '#14509e' : '#ffffff',
                fontSize: '11px',
                fontWeight: 800,
              }}
            >
              03
            </span>
            <span>{t('menu.vendor_management')}</span>
          </Link>

          <Link
            to={URLS.TAX_1099_PAYMENTS}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '0 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: activeTab === 'expenses' ? '#14509e' : 'rgba(255,255,255,.85)',
              background: activeTab === 'expenses' ? '#ffffff' : 'transparent',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: activeTab === 'expenses' ? '0 4px 14px rgba(0,0,0,.1)' : 'none',
              transition: 'all .15s',
            }}
          >
            <span
              style={{
                width: '24px',
                height: '24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                background: activeTab === 'expenses' ? '#edf6fc' : 'rgba(255,255,255,.12)',
                color: activeTab === 'expenses' ? '#14509e' : '#ffffff',
                fontSize: '11px',
                fontWeight: 800,
              }}
            >
              04
            </span>
            <span>{t('menu.payment_ledger')}</span>
          </Link>

          <Link
            to={URLS.TAX_1099_PAYEE_REVIEW}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '0 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: activeTab === 'payees' ? '#14509e' : 'rgba(255,255,255,.85)',
              background: activeTab === 'payees' ? '#ffffff' : 'transparent',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: activeTab === 'payees' ? '0 4px 14px rgba(0,0,0,.1)' : 'none',
              transition: 'all .15s',
            }}
          >
            <span
              style={{
                width: '24px',
                height: '24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                background: activeTab === 'payees' ? '#edf6fc' : 'rgba(255,255,255,.12)',
                color: activeTab === 'payees' ? '#14509e' : '#ffffff',
                fontSize: '11px',
                fontWeight: 800,
              }}
            >
              05
            </span>
            <span>{t('menu.payee_review')}</span>
          </Link>

          <div
            style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#bfdbfe',
              fontWeight: 800,
              padding: '8px 6px 0',
            }}
          >
            {t('section.filing')}
          </div>

          <Link
            to={URLS.TAX_1099_FORMS}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '0 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: activeTab === 'forms' ? '#14509e' : 'rgba(255,255,255,.85)',
              background: activeTab === 'forms' ? '#ffffff' : 'transparent',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: activeTab === 'forms' ? '0 4px 14px rgba(0,0,0,.1)' : 'none',
              transition: 'all .15s',
            }}
          >
            <span
              style={{
                width: '24px',
                height: '24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                background: activeTab === 'forms' ? '#edf6fc' : 'rgba(255,255,255,.12)',
                color: activeTab === 'forms' ? '#14509e' : '#ffffff',
                fontSize: '11px',
                fontWeight: 800,
              }}
            >
              06
            </span>
            <span>{t('menu.forms_1099')}</span>
          </Link>

          <Link
            to={URLS.TAX_1099_YEAR_END_REVIEW}
            style={{
              width: '100%',
              minHeight: '40px',
              padding: '0 12px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              color: activeTab === 'yearend' ? '#14509e' : 'rgba(255,255,255,.85)',
              background: activeTab === 'yearend' ? '#ffffff' : 'transparent',
              fontWeight: 700,
              fontSize: '13px',
              boxShadow: activeTab === 'yearend' ? '0 4px 14px rgba(0,0,0,.1)' : 'none',
              transition: 'all .15s',
            }}
          >
            <span
              style={{
                width: '24px',
                height: '24px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: '6px',
                background: activeTab === 'yearend' ? '#edf6fc' : 'rgba(255,255,255,.12)',
                color: activeTab === 'yearend' ? '#14509e' : '#ffffff',
                fontSize: '11px',
                fontWeight: 800,
              }}
            >
              07
            </span>
            <span>{t('menu.year_end_review')}</span>
          </Link>
        </nav>

        {/* Connection status footer block */}
        <div
          style={{
            marginTop: 'auto',
            padding: '14px',
            borderRadius: '12px',
            background: 'rgba(255,255,255,.10)',
            border: '1px solid rgba(255,255,255,.15)',
            fontSize: '12px',
          }}
        >
          <strong style={{ color: '#ffffff', display: 'block', fontWeight: 700, marginBottom: '4px' }}>
            {t('status.connected')}
          </strong>
          <p style={{ color: '#dbeafe', fontSize: '11px', margin: '0 0 8px 0', lineHeight: 1.4 }}>
            {t('status.description')}
          </p>
          <div
            style={{
              fontSize: '10px',
              color: '#86efac',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#4ade80',
                display: 'inline-block',
                boxShadow: '0 0 6px #4ade80',
              }}
            />
            {t('status.auto_sync')}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-grow-1 p-3 p-md-4" style={{ minWidth: 0, overflowX: 'auto' }}>
        <div style={{ maxWidth: '1450px', margin: '0 auto' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

/**
 * The provider sits above the layout because the layout itself reads payer data
 * for the sidebar. Every 1099 route renders inside here, so all of them share
 * the one fetch.
 */
const Tax1099Layout: React.FC = () => (
  <PayerDataProvider>
    <Tax1099LayoutInner />
  </PayerDataProvider>
);

export default Tax1099Layout;
